import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Menu, X, ArrowLeft, Home, FileText, Shield, Moon, Sun } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Styled components
const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  padding: 0.75rem 1.5rem;
  
  /* Fester Hintergrund */
  background: ${({ theme }) => theme.colors.bg};
  
  /* Leichter Blur-Effekt für bessere Lesbarkeit */
  backdrop-filter: blur(10px);
  
  /* Konstanter leichter Schatten */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0.5rem 1rem;
  }
`;

const NavbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: ${({ $isOpen }) => $isOpen ? 'flex' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => `${theme.colors.bg}fa`};
    backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    z-index: 990;
    padding: 2rem;
    animation: ${fadeIn} 0.3s ease forwards;
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme, $isActive }) => 
    $isActive ? theme.colors.accent : theme.colors.text};
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${({ $isActive }) => $isActive ? '100%' : '0'};
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    
    &::after {
      width: 100%;
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    background: ${({ theme }) => `${theme.colors.card}90`};
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    width: 100%;
    justify-content: center;
    border: 1px solid ${({ theme }) => theme.colors.secondary};
    
    &::after {
      bottom: -8px;
    }
    
    &:hover {
      background: ${({ theme }) => theme.colors.card};
      transform: translateY(-3px);
    }
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ThemeToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.card};
    transform: translateY(-3px);
  }
  
  svg {
    transition: all 0.3s ease;
  }
  
  &:hover svg {
    transform: rotate(15deg);
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideInRight} 0.3s ease forwards;
  
  &:hover {
    background: ${({ theme }) => theme.colors.card};
    transform: translateX(-5px);
  }
  
  span {
    font-weight: 500;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 40px;
    padding: 0.5rem;
    
    span {
      display: none;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  background: none;
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  z-index: 1000;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
  }
`;

// Häufigkeit, mit der der Scroll-Event ausgewertet wird (in ms)
const SCROLL_CHECK_INTERVAL = 100;

const RedesignedNavigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('hero');
  
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  
  // Using the theme context for dark/light mode toggle
  const { isDark, toggleTheme } = useTheme();
  
  // Funktion zur Erkennung des sichtbaren Abschnitts
  const determineActiveSection = () => {
    if (!isHomePage) return null;
    
    const sections = ['hero', 'about', 'learning', 'projects', 'contact'];
    const scrollPosition = window.scrollY + 100; // Offset für die Navigation
    
    // Ermittle für jeden Abschnitt seine Position und Höhe
    const sectionPositions = sections.map(section => {
      const element = document.getElementById(section);
      if (!element) return { id: section, top: 0, bottom: 0 };
      
      const rect = element.getBoundingClientRect();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      return {
        id: section,
        top: rect.top + scrollTop,
        bottom: rect.bottom + scrollTop,
        height: rect.height
      };
    });
    
    // Finde den aktuellen Abschnitt basierend auf der Scroll-Position
    for (const section of sectionPositions) {
      // Wenn wir uns im oberen Bereich des Abschnitts befinden oder
      // wenn der Abschnitt den Viewport füllt
      const isInSection = 
        (scrollPosition >= section.top && scrollPosition < section.bottom) ||
        (section.height > window.innerHeight && 
         scrollPosition >= section.top && 
         scrollPosition < section.top + window.innerHeight);
      
      if (isInSection) {
        return section.id;
      }
    }
    
    // Fallback auf den ersten Abschnitt, wenn keiner aktiv ist
    // (könnte bei sehr schnellem Scrollen passieren)
    return 'hero';
  };
  
  // Effekt für Scroll-Events
  useEffect(() => {
    let scrollTimer = null;
    let lastScrollY = window.scrollY;
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Aktualisiere den scrolled-Status
      if (currentScrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
      
      // Optimiere Performance durch Throttling
      // Wir aktualisieren nur, wenn der Nutzer aufgehört hat zu scrollen
      // oder wenn sich die Scroll-Position signifikant verändert hat
      if (!scrollTimer || Math.abs(currentScrollY - lastScrollY) > 50) {
        if (scrollTimer) clearTimeout(scrollTimer);
        
        scrollTimer = setTimeout(() => {
          const activeSection = determineActiveSection();
          if (activeSection && activeSection !== currentSection) {
            setCurrentSection(activeSection);
            console.log("Aktiver Abschnitt nach Scroll:", activeSection);
          }
          scrollTimer = null;
        }, SCROLL_CHECK_INTERVAL);
        
        lastScrollY = currentScrollY;
      }
    };
    
    // Füge Scroll-Event-Listener für normales Scrollen hinzu
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initialer Check beim Laden der Seite
    handleScroll();
    
    // Kontinuierliche Überprüfung alle 500ms (als Fallback)
    const intervalCheck = setInterval(() => {
      const activeSection = determineActiveSection();
      if (activeSection && activeSection !== currentSection) {
        setCurrentSection(activeSection);
      }
    }, 500);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
      clearInterval(intervalCheck);
    };
  }, [isHomePage, currentSection]);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = !isMenuOpen ? 'hidden' : '';
  };
  
  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = '';
  };
  
  const handleNavClick = (id) => {
    closeMenu();
    
    if (isHomePage) {
      const element = document.getElementById(id);
      if (element) {
        // Scroll zum Element
        element.scrollIntoView({ behavior: 'smooth' });
        
        // Setze den aktiven Abschnitt sofort
        setCurrentSection(id);
      }
    } else {
      navigate(`/#${id}`);
    }
  };
  
  const navigateHome = () => {
    navigate('/');
  };
  
  // Debug-Info in die Konsole
  console.log("Rendering Navigation, current section:", currentSection);
  
  return (
    <NavbarContainer $scrolled={scrolled} $isOpen={isMenuOpen}>
      <NavbarContent>
        <LogoContainer>
          {!isHomePage ? (
            <BackButton onClick={navigateHome} aria-label="Back to home">
              <ArrowLeft size={18} />
              <span>Back</span>
            </BackButton>
          ) : null}
        </LogoContainer>
        
        <NavLinks $isOpen={isMenuOpen} onClick={closeMenu}>
          {isHomePage ? (
            <>
              <NavLink 
                to="#hero"
                $isActive={currentSection === 'hero'}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('hero');
                }}
              >
                <Home size={18} />
                <span>Home</span>
              </NavLink>
              
              <NavLink 
                to="#about"
                $isActive={currentSection === 'about'}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('about');
                }}
              >
                <span>About</span>
              </NavLink>
              
              <NavLink 
                to="#learning"
                $isActive={currentSection === 'learning'}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('learning');
                }}
              >
                <span>Learning</span>
              </NavLink>
              
              <NavLink 
                to="#projects"
                $isActive={currentSection === 'projects'}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('projects');
                }}
              >
                <span>Projects</span>
              </NavLink>
              
              <NavLink 
                to="#contact"
                $isActive={currentSection === 'contact'}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('contact');
                }}
              >
                <span>Contact</span>
              </NavLink>
            </>
          ) : null}
          
          <NavLink to="/resume">
            <FileText size={18} />
            <span>Resume</span>
          </NavLink>
          
          <NavLink to="/privacy">
            <Shield size={18} />
            <span>Privacy</span>
          </NavLink>
        </NavLinks>
        
        <NavActions>
          <ThemeToggleButton 
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </ThemeToggleButton>
          
          <MenuButton 
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MenuButton>
        </NavActions>
      </NavbarContent>
    </NavbarContainer>
  );
};

export default RedesignedNavigation;